import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, avatarSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Heart, ChatCircle, PaperPlaneTilt, Lightning, Tag } from "@phosphor-icons/react";

export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [tag, setTag] = useState("thought");
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/posts");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoadingPosts(false);
    }
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!content.trim()) return;
    setLoading(true);
    try {
      await api.post("/posts", { content, tag });
      setContent("");
      setTag("thought");
      toast.success("Posted");
      load();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (postId) => {
    try {
      const { data } = await api.post(`/posts/${postId}/like`);
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, liked: data.liked, likes: data.likes } : p)));
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="feed-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">The Wall</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">Inside chatter.</h1>
          <p className="text-zinc-500 mt-2 text-sm">Requirements, listings, and thoughts from your network.</p>
        </div>

        {/* Composer */}
        {user && typeof user === "object" && (
          <div className="card-surface rounded-xl p-5 mb-6" data-testid="post-composer">
            <div className="flex items-start gap-3">
              <Avatar className="h-10 w-10 border border-white/10">
                <AvatarImage src={avatarSrc(user.avatar_url)} />
                <AvatarFallback className="bg-ink-700">{user.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Share a requirement, off-market listing, or thought..."
                  className="bg-transparent border-0 focus-visible:ring-0 resize-none px-0 text-base"
                  data-testid="post-textarea"
                  rows={3}
                />
                <div className="flex items-center justify-between pt-3 border-t hairline">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTag("thought")}
                      data-testid="tag-thought"
                      className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-all ${
                        tag === "thought"
                          ? "border-gold text-gold bg-gold/5"
                          : "hairline text-zinc-500 hover:text-zinc-300"
                      }`}
                    >
                      <Lightning size={10} className="inline mr-1" weight="fill" /> Thought
                    </button>
                    <button
                      onClick={() => setTag("requirement")}
                      data-testid="tag-requirement"
                      className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-all ${
                        tag === "requirement"
                          ? "border-gold text-gold bg-gold/5"
                          : "hairline text-zinc-500 hover:text-zinc-300"
                      }`}
                    >
                      <Tag size={10} className="inline mr-1" weight="fill" /> Requirement
                    </button>
                  </div>
                  <Button
                    onClick={post}
                    disabled={loading || !content.trim()}
                    className="bg-gold text-ink-900 hover:bg-gold-light"
                    data-testid="submit-post"
                  >
                    <PaperPlaneTilt size={14} weight="fill" className="mr-1.5" /> Post
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feed */}
        <div className="space-y-4 stagger" data-testid="posts-feed">
          {loadingPosts ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card-surface rounded-xl p-5" data-testid={`post-skeleton-${i}`}>
                <div className="flex items-start gap-3">
                  <Skeleton className="h-11 w-11 rounded-full bg-white/[0.08]" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3 bg-white/[0.08]" />
                    <Skeleton className="h-3 w-1/4 bg-white/[0.05]" />
                    <div className="pt-3 space-y-2">
                      <Skeleton className="h-3 w-full bg-white/[0.06]" />
                      <Skeleton className="h-3 w-5/6 bg-white/[0.06]" />
                      <Skeleton className="h-3 w-3/4 bg-white/[0.06]" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <>
              {posts.map((p) => (
                <PostCard key={p.id} post={p} onLike={() => toggleLike(p.id)} />
              ))}
              {posts.length === 0 && (
                <div className="card-surface rounded-xl p-10 text-center text-zinc-500">
                  No posts yet. Connect with other agents to populate your wall.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, onLike }) {
  const [comments, setComments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const loadComments = async () => {
    if (loaded) return;
    const { data } = await api.get(`/posts/${post.id}/comments`);
    setComments(data);
    setLoaded(true);
  };
  const toggle = async () => {
    setOpen((o) => !o);
    if (!open) await loadComments();
  };
  const addComment = async () => {
    if (!text.trim()) return;
    const { data } = await api.post(`/posts/${post.id}/comments`, { content: text });
    setComments((c) => [...c, data]);
    setText("");
  };

  return (
    <div className="card-surface rounded-xl p-5" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/agents/${post.author?.id}`}>
          <Avatar className="h-11 w-11 border border-white/10">
            <AvatarImage src={avatarSrc(post.author?.avatar_url)} />
            <AvatarFallback className="bg-ink-700">{post.author?.name?.[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/agents/${post.author?.id}`} className="text-sm font-medium hover:text-gold">
              {post.author?.name}
            </Link>
            <span className="text-xs text-zinc-500">· {post.author?.area}{post.author?.city ? `, ${post.author.city}` : ""}</span>
            {post.tag === "requirement" && (
              <Badge className="bg-gold/10 text-gold border border-gold/30 hover:bg-gold/10 text-[10px] uppercase tracking-wider">
                Requirement
              </Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500">{formatTime(post.created_at)}</div>
          <p className="text-zinc-200 mt-3 leading-relaxed whitespace-pre-wrap">{post.content}</p>

          <div className="flex items-center gap-5 mt-4 pt-3 border-t hairline">
            <button
              onClick={onLike}
              data-testid={`like-${post.id}`}
              className={`flex items-center gap-1.5 text-xs transition-colors ${
                post.liked ? "text-gold" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Heart size={16} weight={post.liked ? "fill" : "regular"} />
              <span className="font-mono">{post.likes}</span>
            </button>
            <button
              onClick={toggle}
              data-testid={`toggle-comments-${post.id}`}
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300"
            >
              <ChatCircle size={16} />
              <span className="font-mono">{post.comments_count}</span>
            </button>
          </div>

          {open && (
            <div className="mt-4 space-y-3" data-testid={`comments-${post.id}`}>
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2.5">
                  <Avatar className="h-7 w-7 border border-white/10">
                    <AvatarImage src={avatarSrc(c.author?.avatar_url)} />
                    <AvatarFallback className="bg-ink-700 text-xs">{c.author?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="bg-white/[0.03] rounded-lg px-3 py-2 flex-1">
                    <div className="text-xs">
                      <span className="font-medium">{c.author?.name}</span>
                      <span className="text-zinc-500 ml-2">{formatTime(c.created_at)}</span>
                    </div>
                    <div className="text-sm text-zinc-300 mt-0.5">{c.content}</div>
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addComment()}
                  placeholder="Write a comment..."
                  data-testid={`comment-input-${post.id}`}
                  className="flex-1 bg-ink-700 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-gold"
                />
                <Button
                  onClick={addComment}
                  size="sm"
                  className="bg-gold text-ink-900 hover:bg-gold-light"
                  data-testid={`send-comment-${post.id}`}
                >
                  Send
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}
